<template>
  <v-app-bar
    app
    density="compact"
    :key="compKey"
    :class="{ 'position-fixed': !isMobileViewport || !isEmployeeOnly() }">
    <template #default>
      <div class="nav top-nav d-flex justify-space-around">
        <!-- Title -->
        <router-link
          class="lead"
          to="/">
          Scheduling App
        </router-link>

        <template v-if="userStore.userExists && (!isEmployeeOnly() || !isMobileViewport)">
          <!-- Admin menu -->
          <a
            class="menu"
            data-test="admin-menu"
            v-if="isSchedulingAdminOrHigher()">
            Admin
            <v-menu
              :close-on-content-click="true"
              open-on-hover
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'build-schedule' }">Schedule
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'backups' }">Backups
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'schedule-config' }">Schedule config
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'shift-codes' }">Shift codes
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'user-list', force: true }">User list
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'team-user-defaults', force: true }">Team/User defaults
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'team-admin' }">Team Management
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'banked-hours' }">Banked hours
                  </router-link>
                </v-list-item>
                <v-list-item
                  v-if="isVacationUploadsEnabled"
                  data-test="vacation-uploads">
                  <router-link
                    title=""
                    :to="{ name: 'vacations' }">Vacations
                  </router-link>
                </v-list-item>
              </v-list>
            </v-menu>
          </a>

          <!-- User menu -->
          <a
            class="menu"
            v-if="!isAuditor()">User
            <v-menu
              open-on-hover
              :close-on-content-click="true"
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'bid-entry' }">
                    Bid Entry
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link
                    title=""
                    :to="{ name: 'work-schedule' }">Work Schedule
                  </router-link>
                </v-list-item>
              </v-list>
            </v-menu>
          </a>

          <!-- Reviewer menu -->
          <a
            class="menu"
            v-if="isReviewerOrHigher()">Reviewer
            <v-menu
              :close-on-content-click="true"
              open-on-hover
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <router-link :to="{ name: 'reviewer-schedule' }">Schedule</router-link>
                </v-list-item>
              </v-list>
            </v-menu>
          </a>

          <!-- Live schedule menu -->
          <a
            class="menu"
            v-if="hasLiveScheduleAccess">Live schedule
            <v-menu
              :close-on-content-click="true"
              open-on-hover
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <router-link :to="{ name: 'live-schedule-view' }">Schedule</router-link>
                </v-list-item>
                <v-list-item>
                  <router-link :to="{ name: 'sched-mods' }">Sched Mods</router-link>
                </v-list-item>
              </v-list>
            </v-menu>
          </a>

          <!-- Testing menu -->
          <a
            class="menu"
            v-if="IS_NODE_ENV_DEV || IS_STAGING">Testing
            <v-menu
              :close-on-content-click="true"
              open-on-hover
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <router-link :to="{ name: 'styleguide' }">Style Guide</router-link>
                </v-list-item>

                <v-list-item>
                  <router-link :to="{ name: 'datetime' }">Date & Time</router-link>
                </v-list-item>
              </v-list>
            </v-menu>
          </a>

          <!-- Tools menu -->
          <a
            class="menu"
            v-if="route.path === '/admin/schedule' && !hasCurrSchedPassed"
            data-test="tools-menu">Tools
            <v-menu
              :close-on-content-click="false"
              open-on-hover
              activator="parent"
              class="top-menu">
              <v-list>
                <v-list-item>
                  <ClearWorkforce />
                </v-list-item>

                <v-list-item>
                  <ClearShifts />
                </v-list-item>

                <v-list-item>
                  <ClearBids />
                </v-list-item>

              </v-list>
            </v-menu>
          </a>
        </template>
      </div>

      <!-- Profile initials -->
      <div
        v-if="userStore.userExists"
        class="ml-auto h-100">
        <ProfileView />
      </div>
    </template>
  </v-app-bar>
</template>

<script setup lang="ts">
import {
  isAuditor,
  isEmployeeOnly,
  isLead,
  isManager,
  isReviewerOrHigher,
  isSchedulingAdminOrHigher,
  isSupport,
} from "@/auth/auth.roles";
import ProfileView from "@/components/ProfileView.vue";
import { useUserStore } from "@/stores/user.store";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import ClearBids from "./ClearBids.vue";
import ClearShifts from "./ClearShifts.vue";
import ClearWorkforce from "./ClearWorkforce.vue";
import { useCurrSchedDataIdsStore } from "@/stores/currSchedDataIds.store";
import { hasSchedulePassed } from "@/lib/schedPeriod";
import { IS_STAGING } from "@/router";
import useFeatureFlags from "@/composables/useFeatureFlags";

const { isVacationUploadsEnabled } = useFeatureFlags();

const currSchedDataIdsStore = useCurrSchedDataIdsStore();
const hasCurrSchedPassed = computed(() => hasSchedulePassed(currSchedDataIdsStore.currSchedPeriodId));

const route = useRoute();

const IS_NODE_ENV_DEV = import.meta.env.DEV;

const userStore = useUserStore();
const compKey = ref(0);
const { xs: isMobileViewport } = useDisplay();

const hasLiveScheduleAccess = computed(() => {
  return (isManager() || isLead() || isSupport() || isSchedulingAdminOrHigher());
});
</script>

<style lang="scss" scoped>
a {
  color: blue;
  cursor: pointer;
  text-decoration: none;

  &.menu {
    cursor: default;
    padding-top: 16px;
    margin-bottom: 1px;
  }
}

.top-menu {
  cursor: default;

  > .v-overlay__content {
    > .v-list {
      padding: 0.5rem 0;
      border-radius: 0;
      background-color: var(--c-teal);

      .v-list-item {
        padding: 0;
        min-height: 0;

        :deep(a) {
          width: 100%;
          padding: 0.35rem 1rem;
          display: block;
          font-size: 0.9rem;
          color: white;
          font-weight: var(--f-weight-bold);

          &:hover {
            background-color: var(--c-teal-dark);
          }

          &.vue-w-active-link {
            background-color: var(--c-teal-light);
            color: var(--c-teal-dark);
          }
        }
      }
    }
  }
}
</style>
